import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFileInvoiceDollar, FaDownload, FaExternalLinkAlt } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { signalerErreur } from '../../components/Utils/signalerErreur';

/**
 * « Mes bulletins » de l'espace collaborateur (Yros/Kavero#73, T2 de
 * l'intent YRH-SHARED-007). Consomme C12, relais transparent de C11.
 *
 * Les chemins sont **relatifs** : la `baseURL` d'`axiosInstance` vaut déjà
 * `${API_URL}/proxy/api`, un chemin absolu doublerait le préfixe.
 *
 * L'écran ne retrie rien : C11 rend déjà du plus récent au plus ancien, et
 * retrier ici ferait diverger deux ordres pour la même donnée.
 *
 * Pas de `HeaderCard` — la coquille `EspaceEmploye` le pose déjà. Pas de badge
 * d'état non plus : C11 ne rend que des bulletins `done`, une pastille
 * « validé » n'informerait de rien et serait lue comme un état du dossier.
 */

/**
 * Nom du fichier téléchargé, construit **côté portail** depuis `periode`.
 *
 * Le `Content-Disposition` amont n'est ni relayé par le proxy (`app.py:431`
 * ne recopie que le `Content-Type`) ni lisible par le navigateur (pas
 * d'`expose_headers` CORS) : il n'est jamais consulté.
 */
function nomFichier(periode) {
  return `bulletin-${periode}.pdf`;
}

/**
 * « juillet 2026 » plutôt que « 2026-07 », dans la langue de l'interface.
 *
 * Repli sur la valeur brute quand la période est vide ou hors format : sans
 * lui, `Intl` écrirait « Invalid Date » en pleine table. Ce libellé ne change
 * jamais le nom du fichier, qui reste en AAAA-MM.
 */
function libellePeriode(periode, locale) {
  const date = new Date(`${periode}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return periode;
  }
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
}

// Le temps laissé au nouvel onglet pour lire le PDF avant de libérer l'URL.
const DUREE_URL_OUVERTE_MS = 60000;

function MesBulletins() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const [bulletins, setBulletins] = useState([]);
  const [chargement, setChargement] = useState(true);
  // Drapeaux, pas messages : le libellé se calcule au rendu, sinon un
  // changement de langue laisserait l'erreur figée dans l'ancienne.
  const [erreur, setErreur] = useState(false);
  const [erreurTelechargement, setErreurTelechargement] = useState(false);
  // « Ouvrir » refusé par le navigateur : le bulletin est téléchargé à la place.
  const [ongletBloque, setOngletBloque] = useState(false);
  const [enCoursId, setEnCoursId] = useState(null);

  useEffect(() => {
    let actif = true;
    (async () => {
      try {
        const reponse = await apiService.getElement('/rh/bulletins');
        if (!actif) return;
        setBulletins(reponse?.data?.bulletins || []);
      } catch (e) {
        if (!actif) return;
        signalerErreur(e, 'MesBulletins.chargerBulletins');
        setBulletins([]);
        setErreur(true);
      } finally {
        if (actif) setChargement(false);
      }
    })();
    return () => {
      actif = false;
    };
  }, []);

  const telecharger = useCallback(async (bulletin) => {
    setEnCoursId(bulletin.id);
    setErreurTelechargement(false);
    setOngletBloque(false);
    try {
      const reponse = await apiService.getBlobFile(`/rh/bulletins/${bulletin.id}/pdf`);
      apiService.downloadBlob(
        new Blob([reponse.data], { type: 'application/pdf' }),
        nomFichier(bulletin.periode)
      );
    } catch (e) {
      // Le corps d'erreur arrive en `blob` : il n'est pas lisible en JSON, et
      // l'afficher rendrait « [object Blob] ». Le message est toujours local.
      signalerErreur(e, 'MesBulletins.telecharger');
      setErreurTelechargement(true);
    } finally {
      setEnCoursId(null);
    }
  }, []);

  /**
   * « Ouvrir » (Yros/Kavero#488) : le PDF dans un nouvel onglet, lu par le
   * lecteur du navigateur ou du téléphone. L'onglet s'ouvre **avant** l'appel :
   * ouvert après un `await`, il n'est plus rattaché au clic et les navigateurs
   * mobiles le bloquent. Si le navigateur refuse tout de même l'onglet, le
   * PDF se télécharge et un message le dit (revue de payor_portail#479).
   */
  const ouvrir = useCallback(async (bulletin) => {
    const onglet = window.open('', '_blank');
    setEnCoursId(bulletin.id);
    setErreurTelechargement(false);
    setOngletBloque(false);
    try {
      const reponse = await apiService.getBlobFile(`/rh/bulletins/${bulletin.id}/pdf`);
      const blob = new Blob([reponse.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (onglet) {
        onglet.location.href = url;
      } else if (!window.open(url, '_blank')) {
        URL.revokeObjectURL(url);
        apiService.downloadBlob(blob, nomFichier(bulletin.periode));
        setOngletBloque(true);
        return;
      }
      setTimeout(() => URL.revokeObjectURL(url), DUREE_URL_OUVERTE_MS);
    } catch (e) {
      if (onglet) onglet.close();
      signalerErreur(e, 'MesBulletins.ouvrir');
      setErreurTelechargement(true);
    } finally {
      setEnCoursId(null);
    }
  }, []);

  const colonnes = [
    {
      key: 'periode',
      label: t('mesBulletins.colPeriode'),
      render: (row) => (
        <span
          data-testid={`mes-bulletins-periode-${row.id}`}
          className="font-medium text-slate-800"
        >
          {libellePeriode(row.periode, locale)}
        </span>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-100">
      <div className="flex items-center gap-2 mb-3.5 pb-2.5 border-b border-slate-100">
        <FaFileInvoiceDollar
          className="w-4 h-4 shrink-0 text-[--color-primary]"
          aria-hidden="true"
        />
        <h3 className="text-sm sm:text-base font-bold text-slate-800 tracking-tight">
          {t('mesBulletins.titre')}
        </h3>
      </div>

      {erreur && (
        <p
          data-testid="mes-bulletins-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {t('mesBulletins.erreurListe')}
        </p>
      )}

      {erreurTelechargement && (
        <p
          data-testid="mes-bulletins-erreur-telechargement"
          className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
        >
          {t('mesBulletins.erreurTelechargement')}
        </p>
      )}

      {ongletBloque && (
        <p
          data-testid="mes-bulletins-onglet-bloque"
          className="mb-3 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
        >
          {t('mesBulletins.ongletBloque')}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : erreur ? null : bulletins.length === 0 ? (
        // `TableList` n'est pas monté à vide : son état vide dit « Aucun
        // résultat trouvé / Essayez de modifier vos critères », un message de
        // recherche qui contredit le critère « un message le lui dit ».
        <p data-testid="mes-bulletins-vide" className="py-8 text-center text-sm text-gray-500">
          {t('mesBulletins.vide')}
        </p>
      ) : (
        <TableList
          columns={colonnes}
          data={bulletins}
          searchDisplay={false}
          extraActions={(row) => (
            <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid={`mes-bulletins-ouvrir-${row.id}`}
              onClick={() => ouvrir(row)}
              disabled={enCoursId === row.id}
              title={t('mesBulletins.ouvrir')}
              aria-label={t('mesBulletins.ouvrir')}
              className="inline-flex items-center gap-2 text-[--color-primary] hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
            >
              <FaExternalLinkAlt className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t('mesBulletins.ouvrir')}</span>
            </button>
            <button
              type="button"
              data-testid={`mes-bulletins-telecharger-${row.id}`}
              onClick={() => telecharger(row)}
              // Garde contre le double clic : React ne dispatche aucun clic
              // sur un bouton désactivé.
              disabled={enCoursId === row.id}
              title={t('mesBulletins.telecharger')}
              aria-label={t('mesBulletins.telecharger')}
              className="inline-flex items-center gap-2 text-[--color-primary] hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
            >
              <FaDownload className="w-4 h-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t('mesBulletins.telecharger')}</span>
            </button>
            </div>
          )}
        />
      )}
    </div>
  );
}

export default MesBulletins;
